<div class="projectable-form-wrapper" tabindex="0">
    <lib-form-header
        [actionButtonText]="'Save'"
        [data]="{}"
        [moreActions]="[]"
        [multiActions]="[]"
        [saveDisabled]="isCreating || templates.length === 0"
        [showHeaderToggle]="false"
        [title]="'Create A Service From Template'"
        (actionRequested)="onHeaderAction($event)"
    ></lib-form-header>

    @if (!creationFinished) {
    <div class="edge-router-form-container projectable-form-container">
        <div class="projectable-form-main-column">
            <div class="form-group-row">
                <div class="form-group-column three-fifths">
                    <div class="section-card tag-field-container">
                        <div class="section-card-header">
                            <div class="section-icon">
                                <mat-icon>dashboard_customize</mat-icon>
                            </div>
                            <div class="section-header-text">
                                <h3>Template <span class="field-badge required">REQUIRED</span></h3>
                                <p>Choose the blueprint this service is created from</p>
                            </div>
                        </div>

                        <div class="sub-field">
                            @if (templates.length === 0) {
                            <div class="empty-templates-hint">
                                No templates have been created yet.
                                <a (click)="goToTemplates()" class="template-link">Create a template</a>
                                to get started.
                            </div>
                            } @else {
                            <input
                                (input)="onTemplateSearch($any($event.target).value)"
                                [value]="templateSearch"
                                aria-label="Search templates"
                                autocomplete="off"
                                class="template-search"
                                id="SearchServiceTemplate"
                                placeholder="Search templates..."
                                type="text"
                            />
                            <div
                                aria-label="Template"
                                class="template-options"
                                id="SelectServiceTemplate"
                                role="radiogroup"
                            >
                                @for (template of visibleTemplates; track template.id) {
                                <button
                                    (click)="selectTemplate(template.id)"
                                    [attr.aria-checked]="template.id === selectedTemplateId"
                                    [class.selected]="template.id === selectedTemplateId"
                                    class="template-option"
                                    id="SelectServiceTemplate{{ template.id }}"
                                    role="radio"
                                    type="button"
                                >
                                    <span class="template-option-mark" aria-hidden="true"></span>
                                    <span class="template-option-text">
                                        <span class="template-option-title">{{ template.name }}</span>
                                        @if (template.description) {
                                        <span class="template-option-detail">{{ template.description }}</span>
                                        }
                                    </span>
                                </button>
                                } @empty {
                                <p class="template-no-results">No templates match "{{ templateSearch }}"</p>
                                }
                            </div>
                            @if (hiddenTemplateCount > 0) {
                            <button (click)="showMoreTemplates()" class="template-show-more" type="button">
                                Show more ({{ hiddenTemplateCount }} more)
                                <svg
                                    aria-hidden="true"
                                    fill="none"
                                    stroke="currentColor"
                                    stroke-linecap="round"
                                    stroke-linejoin="round"
                                    stroke-width="2"
                                    viewBox="0 0 24 24"
                                >
                                    <path d="m6 9 6 6 6-6" />
                                </svg>
                            </button>
                            }
                            }
                        </div>
                    </div>

                    @if (selectedTemplate) {
                    <div class="section-card tag-field-container">
                        <div class="section-card-header">
                            <div class="section-icon">
                                <mat-icon>description</mat-icon>
                            </div>
                            <div class="section-header-text">
                                <h3>Service</h3>
                                <p>Name the service and set its options</p>
                            </div>
                        </div>
                        <div class="sub-field">
                            <label class="sub-field-label"
                                >Service Name <span class="field-badge required">REQUIRED</span></label
                            >
                            <input
                                (ngModelChange)="onNameChange($event)"
                                [ngModel]="serviceName"
                                [ngClass]="{ error: nameError }"
                                [attr.aria-invalid]="!!nameError"
                                [attr.aria-describedby]="nameError ? 'ServiceTemplateServiceNameError' : null"
                                class="form-field-input"
                                id="ServiceTemplateServiceName"
                                placeholder="Name this service"
                            />
                            @if (nameError) {
                            <span class="form-field-error" id="ServiceTemplateServiceNameError" role="alert">{{
                                nameError
                            }}</span>
                            }
                        </div>

                        <div class="options-subhead">Options</div>

                        @if (selectedTemplate.serviceFields.includeRoleAttributes) {
                        <div class="sub-field">
                            <label class="sub-field-label"
                                >Service Role Attributes <span class="field-badge optional">OPTIONAL</span></label
                            >
                            <lib-tag-selector
                                [availableRoleAttributes]="availableRoleAttributes"
                                [(selectedRoleAttributes)]="serviceRoleAttributes"
                                [placeholder]="'Add a role attribute'"
                            ></lib-tag-selector>
                        </div>
                        }

                        <div class="sub-field-row">
                            <div class="sub-field">
                                <label class="sub-field-label">
                                    <span class="sub-field-label-text">
                                        Encryption Required
                                        <span
                                            [matTooltip]="
                                                'When enabled, traffic for this service must be end-to-end encrypted between the dialing and hosting identities.'
                                            "
                                            aria-label="Encryption required — more info"
                                            class="infoicon"
                                            matTooltipPosition="below"
                                            role="img"
                                            tabindex="0"
                                        ></span>
                                    </span>
                                </label>
                                <button
                                    (click)="encryptionRequired = !encryptionRequired"
                                    [class.on]="encryptionRequired"
                                    [attr.aria-checked]="encryptionRequired"
                                    aria-label="Encryption required"
                                    class="nf-switch"
                                    id="ServiceTemplateEncryptionRequired"
                                    role="switch"
                                    type="button"
                                >
                                    <span class="nf-switch-track"><span class="nf-switch-thumb"></span></span>
                                    <span class="nf-switch-text">{{ encryptionRequired ? 'YES' : 'NO' }}</span>
                                </button>
                            </div>

                            <div class="sub-field">
                                <label class="sub-field-label">
                                    <span class="sub-field-label-text">
                                        Terminator Strategy
                                        <span
                                            [matTooltip]="
                                                'How traffic is routed across multiple terminators/instances hosting this service.'
                                            "
                                            aria-label="Terminator strategy — more info"
                                            class="infoicon"
                                            matTooltipPosition="below"
                                            role="img"
                                            tabindex="0"
                                        ></span>
                                    </span>
                                </label>
                                <select
                                    [(ngModel)]="terminatorStrategy"
                                    class="form-field-input"
                                    id="ServiceTemplateTerminatorStrategy"
                                >
                                    <option value="smartrouting">smartrouting</option>
                                    <option value="ha">ha</option>
                                    <option value="weighted">weighted</option>
                                    <option value="random">random</option>
                                </select>
                            </div>
                        </div>
                    </div>
                    } @if (selectedTemplate) {
                    <div class="section-card tag-field-container">
                        <div class="section-card-header">
                            <div class="section-icon">
                                <mat-icon>security</mat-icon>
                            </div>
                            <div class="section-header-text">
                                <h3>Access Policies</h3>
                                <p>Grant identities permission to use or host this service. Leave blank to add policies later.</p>
                            </div>
                        </div>

                        <div class="sub-field">
                            <label class="sub-field-label access-field-label">
                                Who can connect
                                <span class="access-dir dial">Dial</span>
                            </label>
                            <p class="access-field-help">Identities with any of these attributes can reach this service.</p>
                            <lib-tag-selector
                                [availableRoleAttributes]="availableRoleAttributes"
                                [(selectedRoleAttributes)]="dialPolicyAttributes"
                                [placeholder]="'Add an identity attribute'"
                            ></lib-tag-selector>
                        </div>

                        <div class="sub-field">
                            <label class="sub-field-label access-field-label">
                                Who can host
                                <span class="access-dir bind">Bind</span>
                            </label>
                            <p class="access-field-help">Identities with any of these attributes can host (serve) this service.</p>
                            <lib-tag-selector
                                [availableRoleAttributes]="availableRoleAttributes"
                                [(selectedRoleAttributes)]="bindPolicyAttributes"
                                [placeholder]="'Add an identity attribute'"
                            ></lib-tag-selector>
                        </div>
                    </div>
                    } @if (selectedTemplate && hasVisibleConfigTypes) {
                    <div class="section-card tag-field-container service-config-section">
                        <div class="section-card-header">
                            <div class="section-icon">
                                <mat-icon>tune</mat-icon>
                            </div>
                            <div class="section-header-text">
                                <h3>Service Configuration</h3>
                                <p class="section-subtext">Fill in the values each config type needs for this service.</p>
                            </div>
                        </div>

                        <div class="service-config-list">
                            @for (ct of selectedTemplate.configTypes; track ct.configTypeId) { @if
                            (getVisibleFields(ct).length > 0) {
                            <div class="config-subpanel" [class.is-collapsed]="isConfigCollapsed(ct.configTypeId)">
                                <button
                                    type="button"
                                    class="config-subpanel-head"
                                    (click)="toggleConfigCollapsed(ct.configTypeId)"
                                    [attr.aria-expanded]="!isConfigCollapsed(ct.configTypeId)"
                                >
                                    <span class="config-subpanel-chevron" aria-hidden="true"></span>
                                    <span class="config-subpanel-name">{{ ct.configTypeName }}</span>
                                </button>

                        <div class="config-type-fields" [hidden]="isConfigCollapsed(ct.configTypeId)">
                            @for (group of getFieldGroups(ct); track $index) { @if (group.title) {
                            <div class="field-group-head">
                                <span>{{ group.title }}</span>
                                @if (group.required) {
                                <span class="field-badge required">REQUIRED</span>
                                } @else {
                                <span class="field-badge optional">OPTIONAL</span>
                                }
                            </div>
                            } @if (group.endpoint) {
                            <div class="endpoint-row">
                                @if (group.endpoint.protocol; as pf) {
                                <div class="ep-field ep-field--proto">
                                    <span
                                        class="field-label"
                                        [id]="fieldDomId(ct.configTypeId, pf.fieldPath) + '-lbl'"
                                        >{{ pf.label }}</span
                                    >
                                    <select
                                        (change)="
                                            setFieldValue(ct.configTypeId, pf.fieldPath, $any($event.target).value)
                                        "
                                        [attr.aria-invalid]="hasFieldError(ct.configTypeId, pf.fieldPath)"
                                        [attr.aria-labelledby]="fieldDomId(ct.configTypeId, pf.fieldPath) + '-lbl'"
                                        [id]="fieldDomId(ct.configTypeId, pf.fieldPath)"
                                        class="form-field-input"
                                    >
                                        <option value="">—</option>
                                        @for (opt of group.endpoint.protocolOptions; track opt) {
                                        <option
                                            [selected]="getFieldValue(ct.configTypeId, pf.fieldPath) === opt"
                                            [value]="opt"
                                        >
                                            {{ opt }}
                                        </option>
                                        }
                                    </select>
                                </div>
                                } @if (group.endpoint.address; as af) {
                                <div class="ep-field ep-field--addr">
                                    <span
                                        class="field-label"
                                        [id]="fieldDomId(ct.configTypeId, af.fieldPath) + '-lbl'"
                                        >{{ af.label }}</span
                                    >
                                    <input
                                        (change)="
                                            setFieldValue(ct.configTypeId, af.fieldPath, $any($event.target).value)
                                        "
                                        [attr.aria-invalid]="hasFieldError(ct.configTypeId, af.fieldPath)"
                                        [attr.aria-labelledby]="fieldDomId(ct.configTypeId, af.fieldPath) + '-lbl'"
                                        [id]="fieldDomId(ct.configTypeId, af.fieldPath)"
                                        [value]="getFieldValue(ct.configTypeId, af.fieldPath) ?? ''"
                                        class="form-field-input"
                                        placeholder="e.g. backend.internal"
                                        type="text"
                                    />
                                </div>
                                } @if (group.endpoint.port; as pof) {
                                <span class="ep-join">:</span>
                                <div class="ep-field ep-field--port">
                                    <span
                                        class="field-label"
                                        [id]="fieldDomId(ct.configTypeId, pof.fieldPath) + '-lbl'"
                                        >{{ pof.label }}</span
                                    >
                                    <input
                                        (change)="
                                            setFieldValue(ct.configTypeId, pof.fieldPath, $any($event.target).value)
                                        "
                                        [attr.aria-invalid]="hasFieldError(ct.configTypeId, pof.fieldPath)"
                                        [attr.aria-labelledby]="fieldDomId(ct.configTypeId, pof.fieldPath) + '-lbl'"
                                        [id]="fieldDomId(ct.configTypeId, pof.fieldPath)"
                                        [value]="getFieldValue(ct.configTypeId, pof.fieldPath) ?? ''"
                                        class="form-field-input"
                                        inputmode="numeric"
                                        min="0"
                                        placeholder="443"
                                        type="number"
                                    />
                                </div>
                                }
                            </div>
                            @if ( group.endpoint.address && hasFieldError(ct.configTypeId,
                            group.endpoint.address.fieldPath)) {
                            <span class="form-field-error" role="alert">{{
                                getFieldError(ct.configTypeId, group.endpoint.address.fieldPath)
                            }}</span>
                            } } @else if (group.forwarding) {
                            <div class="fwd-group">
                                @for (pair of group.pairs; track pair.control.fieldPath) {
                                <div class="fwd-row">
                                    <div class="fwd-control">
                                        <div class="fwd-control-head">
                                            <button
                                                (click)="
                                                    setFieldValue(
                                                        ct.configTypeId,
                                                        pair.control.fieldPath,
                                                        !isFieldOn(ct.configTypeId, pair.control.fieldPath)
                                                    )
                                                "
                                                [class.on]="isFieldOn(ct.configTypeId, pair.control.fieldPath)"
                                                [attr.aria-checked]="isFieldOn(ct.configTypeId, pair.control.fieldPath)"
                                                [attr.aria-labelledby]="
                                                    fieldDomId(ct.configTypeId, pair.control.fieldPath) + '-lbl'
                                                "
                                                class="nf-switch nf-switch--bare"
                                                role="switch"
                                                type="button"
                                            >
                                                <span class="nf-switch-track"
                                                    ><span class="nf-switch-thumb"></span
                                                ></span>
                                            </button>
                                            <span
                                                class="fwd-control-label"
                                                [class.on]="isFieldOn(ct.configTypeId, pair.control.fieldPath)"
                                                [id]="fieldDomId(ct.configTypeId, pair.control.fieldPath) + '-lbl'"
                                                >{{ pair.control.label }}</span
                                            >
                                            @if (pair.control.description) {
                                            <span
                                                [attr.aria-label]="pair.control.label + ' — more info'"
                                                [matTooltip]="pair.control.description"
                                                class="infoicon"
                                                matTooltipPosition="below"
                                                role="img"
                                                tabindex="0"
                                            ></span>
                                            }
                                        </div>
                                    </div>
                                    <div class="fwd-dependents">
                                        @if (isFieldOn(ct.configTypeId, pair.control.fieldPath)) { @for (dep of
                                        pair.dependents; track dep.fieldPath) {
                                        <ng-container
                                            *ngTemplateOutlet="fieldItem; context: { field: dep, ct: ct }"
                                        ></ng-container>
                                        } } @else {
                                        <span class="fwd-off-note"
                                            >{{ pair.dependents[0]?.label }} — set when {{ pair.control.label }} is
                                            on</span
                                        >
                                        }
                                    </div>
                                </div>
                                }
                            </div>
                            } @else {
                            <div class="field-grid">
                                @for (field of group.fields; track field.fieldPath) {
                                <ng-container
                                    *ngTemplateOutlet="fieldItem; context: { field: field, ct: ct, first: $first }"
                                ></ng-container>
                                }
                            </div>
                            } }
                        </div>

                        <ng-template #fieldItem let-field="field" let-ct="ct" let-first="first">
                            <div
                                [class.field-item-wide]="
                                    field.arrayItemType === 'object' &&
                                    !!field.arrayItemProperties &&
                                    !isPortRangeField(field)
                                "
                                [class.span-2]="field.type === 'array' || (first && field.type === 'string')"
                                [id]="fieldDomId(ct.configTypeId, field.fieldPath)"
                                class="field-cell"
                            >
                                <span class="field-label" [id]="fieldDomId(ct.configTypeId, field.fieldPath) + '-lbl'">
                                    {{ field.label }}
                                    @if (field.required) {
                                    <span class="field-badge required">REQUIRED</span>
                                    } @if (field.description) {
                                    <span
                                        [matTooltip]="field.description"
                                        [attr.aria-label]="field.label + ' — more info'"
                                        class="infoicon"
                                        matTooltipPosition="below"
                                        role="img"
                                        tabindex="0"
                                    ></span>
                                    }
                                </span>
                                <div class="field-control">
                                    @switch (field.type) { @case ('boolean') {
                                    <button
                                        (click)="
                                            setFieldValue(
                                                ct.configTypeId,
                                                field.fieldPath,
                                                getFieldValue(ct.configTypeId, field.fieldPath) !== true
                                            )
                                        "
                                        [class.on]="getFieldValue(ct.configTypeId, field.fieldPath) === true"
                                        [attr.aria-checked]="getFieldValue(ct.configTypeId, field.fieldPath) === true"
                                        [attr.aria-labelledby]="fieldDomId(ct.configTypeId, field.fieldPath) + '-lbl'"
                                        class="nf-switch"
                                        role="switch"
                                        type="button"
                                    >
                                        <span class="nf-switch-track"><span class="nf-switch-thumb"></span></span>
                                        <span class="nf-switch-text">{{
                                            getFieldValue(ct.configTypeId, field.fieldPath) === true ? 'YES' : 'NO'
                                        }}</span>
                                    </button>
                                    } @case ('array') {
                                    <div class="array-editor">
                                        @if (field.enumOptions?.length) {
                                        <div class="enum-options">
                                            @for (option of field.enumOptions; track option) {
                                            <label class="enum-option">
                                                <input
                                                    (change)="
                                                        toggleEnumOption(
                                                            ct.configTypeId,
                                                            field.fieldPath,
                                                            option,
                                                            $event
                                                        )
                                                    "
                                                    [checked]="
                                                        isEnumOptionSelected(ct.configTypeId, field.fieldPath, option)
                                                    "
                                                    type="checkbox"
                                                />
                                                <span>{{ option }}</span>
                                            </label>
                                            }
                                        </div>
                                        } @else if (isPortRangeField(field)) {
                                        <app-chip-tokens
                                            [ngModel]="getPortRangeChips(ct.configTypeId, field.fieldPath)"
                                            (ngModelChange)="
                                                setPortRangeChips(ct.configTypeId, field.fieldPath, $event)
                                            "
                                            [placeholder]="'e.g. 80 or 120-125, press Enter'"
                                            class="form-field-input"
                                        ></app-chip-tokens>
                                        } @else if (field.arrayItemType === 'object' && field.arrayItemProperties) {
                                        <div class="array-object-input">
                                            <div class="object-props-row">
                                                @for (prop of field.arrayItemProperties | keyvalue; track prop.key) {
                                                <div class="object-prop-input">
                                                    <label [title]="prop.key">{{ formatFieldLabel(prop.key) }}</label>
                                                    @if (prop.value.enumOptions?.length) {
                                                    <select
                                                        (change)="
                                                            setTempObjectValue(
                                                                fieldKey(ct, field),
                                                                prop.key,
                                                                $any($event.target).value
                                                            )
                                                        "
                                                        [value]="getTempObjectValue(fieldKey(ct, field), prop.key)"
                                                    >
                                                        <option value="">Select...</option>
                                                        @for (opt of prop.value.enumOptions; track opt) {
                                                        <option [value]="opt">{{ opt }}</option>
                                                        }
                                                    </select>
                                                    } @else {
                                                    <input
                                                        (input)="
                                                            setTempObjectValue(
                                                                fieldKey(ct, field),
                                                                prop.key,
                                                                $any($event.target).value
                                                            )
                                                        "
                                                        [placeholder]="'Enter ' + formatFieldLabel(prop.key)"
                                                        [type]="
                                                            prop.value.type === 'number' ||
                                                            prop.value.type === 'integer'
                                                                ? 'number'
                                                                : 'text'
                                                        "
                                                        [value]="getTempObjectValue(fieldKey(ct, field), prop.key)"
                                                    />
                                                    }
                                                </div>
                                                }
                                            </div>
                                            <button
                                                (click)="addArrayItem(ct.configTypeId, field)"
                                                class="add-btn"
                                                type="button"
                                            >
                                                Add Item
                                            </button>
                                        </div>
                                        @if (getArrayItems(ct.configTypeId, field.fieldPath).length > 0) {
                                        <div class="chip-list">
                                            @for (item of getArrayItems(ct.configTypeId, field.fieldPath); track $index)
                                            {
                                            <span class="array-pill">
                                                {{ formatArrayItem(item) }}
                                                <button
                                                    (click)="removeArrayItem(ct.configTypeId, field.fieldPath, $index)"
                                                    class="remove-pill"
                                                    type="button"
                                                >
                                                    &times;
                                                </button>
                                            </span>
                                            }
                                        </div>
                                        } } @else {
                                        <app-chip-tokens
                                            [ngModel]="getArrayItems(ct.configTypeId, field.fieldPath)"
                                            (ngModelChange)="setFieldValue(ct.configTypeId, field.fieldPath, $event)"
                                            [placeholder]="'Add value and press Enter'"
                                            class="form-field-input"
                                        ></app-chip-tokens>
                                        }
                                    </div>
                                    } @default { @if (field.enumOptions?.length) {
                                    <select
                                        (change)="
                                            setFieldValue(ct.configTypeId, field.fieldPath, $any($event.target).value)
                                        "
                                        [ngClass]="{ error: hasFieldError(ct.configTypeId, field.fieldPath) }"
                                        [attr.aria-invalid]="hasFieldError(ct.configTypeId, field.fieldPath)"
                                        [attr.aria-labelledby]="fieldDomId(ct.configTypeId, field.fieldPath) + '-lbl'"
                                        class="form-field-input"
                                    >
                                        <option value="">Select {{ field.label }}...</option>
                                        @for (opt of field.enumOptions; track opt) {
                                        <option
                                            [selected]="getFieldValue(ct.configTypeId, field.fieldPath) === opt"
                                            [value]="opt"
                                        >
                                            {{ opt }}
                                        </option>
                                        }
                                    </select>
                                    } @else {
                                    <input
                                        (change)="
                                            setFieldValue(ct.configTypeId, field.fieldPath, $any($event.target).value)
                                        "
                                        [ngClass]="{ error: hasFieldError(ct.configTypeId, field.fieldPath) }"
                                        [attr.aria-invalid]="hasFieldError(ct.configTypeId, field.fieldPath)"
                                        [attr.aria-labelledby]="fieldDomId(ct.configTypeId, field.fieldPath) + '-lbl'"
                                        [type]="field.type === 'integer' || field.type === 'number' ? 'number' : 'text'"
                                        [attr.inputmode]="
                                            field.type === 'integer' || field.type === 'number' ? 'numeric' : null
                                        "
                                        [attr.min]="field.type === 'integer' || field.type === 'number' ? 0 : null"
                                        [value]="getFieldValue(ct.configTypeId, field.fieldPath) ?? ''"
                                        class="form-field-input"
                                    />
                                    } } } @if (hasFieldError(ct.configTypeId, field.fieldPath)) {
                                    <span class="form-field-error" role="alert">{{
                                        getFieldError(ct.configTypeId, field.fieldPath)
                                    }}</span>
                                    }
                                </div>
                            </div>
                        </ng-template>
                    </div>
                            } }
                        </div>
                    </div>
                    }
                </div>

                <div class="form-group-column two-fifths">
                    <div class="template-preview">
                        <div class="template-preview-header">
                            <div class="preview-template-name">{{ serviceName || 'Untitled service' }}</div>
                            @if (selectedTemplate) {
                            <a
                                [routerLink]="['/service-templates', selectedTemplate.id]"
                                class="preview-template-link"
                                id="PreviewTemplateLink"
                            >
                                <span class="preview-template-link-prefix">From template</span>
                                <span class="preview-template-link-name">{{ selectedTemplate.name }}</span>
                            </a>
                            }
                        </div>

                        @if (!selectedTemplate) {
                        <div class="preview-placeholder">Select a template to preview this service</div>
                        } @else {
                        <div class="preview-config-block">
                            <div class="preview-config-title-row">
                                <span class="preview-config-title">Service Settings</span>
                            </div>
                            <div class="preview-row">
                                <span class="preview-key">Encryption Required</span>
                                <span class="preview-val">{{ encryptionRequired ? 'Yes' : 'No' }}</span>
                            </div>
                            <div class="preview-row">
                                <span class="preview-key">Terminator Strategy</span>
                                <span class="preview-val">{{ terminatorStrategy }}</span>
                            </div>
                        </div>

                        @for (ct of selectedTemplate.configTypes; track ct.configTypeId) { @if
                        (getVisibleFields(ct).length > 0) {
                        <div class="preview-config-block">
                            <div class="preview-config-title-row">
                                <span class="preview-config-title">{{ ct.configTypeName }}</span>
                            </div>
                            @for (field of getVisibleFields(ct); track field.fieldPath) {
                            <div class="preview-row">
                                <span class="preview-key">{{ field.label }}</span>
                                <span class="preview-val">
                                    @if (isPreviewValueEmpty(ct.configTypeId, field)) {
                                    <span class="preview-empty">Not set</span>
                                    } @else if (isPreviewChipField(field)) { @for (chip of
                                    getPreviewChips(ct.configTypeId, field); track $index) {
                                    <span class="preview-chip">{{ chip }}</span>
                                    } } @else {
                                    {{ formatPreviewValue(ct.configTypeId, field) }}
                                    }
                                </span>
                            </div>
                            }
                        </div>
                        } } }
                    </div>
                </div>
            </div>
        </div>
    </div>
    } @if (creationFinished) {
    <div class="edge-router-form-container projectable-form-container" id="CreationStatusStage">
        <div class="projectable-form-main-column form-group-row">
            <div class="form-group-column three-fifths">
                <h2>{{ hasErrors ? 'Service Creation Completed With Errors' : 'Service Created' }}</h2>

                <ul class="creation-status-list">
                    @for (status of creationStatuses; track status.step) {
                    <li [ngClass]="'status-' + status.status">
                        <span class="status-icon"></span>
                        <span class="status-step">{{ status.step }}</span>
                        @if (status.message) {
                        <span class="status-message">{{ status.message }}</span>
                        } @if (status.status === 'error') {
                        <button (click)="toggleErrorDetails(status)" class="add-btn" type="button">Details</button>
                        } @if (status.showDetails) {
                        <pre class="status-error-details">{{ formatErrorDetails(status.errorDetails) }}</pre>
                        }
                    </li>
                    }
                </ul>

                @if (isWorklist) {
                @if (!hasErrors) {
                <p class="creation-worklist-progress">
                    @if (worklistRemaining > 0) {
                    {{ worklistRemaining }} {{ worklistRemaining === 1 ? 'destination' : 'destinations' }} left in this
                    segment.
                    } @else {
                    That was the last destination in this segment.
                    }
                </p>
                }
                <button (click)="backToWorklist()" class="add-btn" id="BackToDestinationsButton" type="button">
                    Back to destinations
                </button>
                } @else {
                <button (click)="goToServices()" class="add-btn" id="GoToServicesButton" type="button">
                    Go To Services
                </button>
                }
            </div>
        </div>
    </div>
    }
</div>
